import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCalendarCheck, 
    faUserTie, 
    faTags, 
    faChevronRight, 
    faChevronLeft, 
    faCheckCircle, 
    faClock, 
    faEnvelope,
    faIdCard
} from '@fortawesome/free-solid-svg-icons';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';
import './WebBooking.css';

/**
 * WebBookingWizard - Premium Public Booking Experience
 */
const WebBookingWizard = ({ ipserv, sucursalId }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    
    // Selections
    const [services, setServices] = useState([]);
    const [specialties, setSpecialties] = useState([]);
    const [specialists, setSpecialists] = useState([]);
    const [slots, setSlots] = useState([]);
    
    const [selection, setSelection] = useState({
        service: null,
        specialty: null,
        specialist: null,
        date: new Date().toISOString().split('T')[0],
        time: null,
        client: {
            email: '',
            name: '',
            dni: ''
        }
    });

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

    // Step 1: Load Services
    useEffect(() => {
        if (sucursalId) {
            setLoading(true);
            request.Get(API_BASE + 'web/services', { sucursalId }, r => {
                if (r.success) setServices(r.data || []);
                setLoading(false);
            });
        }
    }, [sucursalId]);

    // Navigation logic
    const nextStep = () => setStep(step + 1);
    const prevStep = () => setStep(step - 1);

    const handleServiceSelect = (service) => {
        setSelection({ ...selection, service, specialty: null, specialist: null, time: null });
        setLoading(true);
        request.Get(API_BASE + 'web/specialties', { serviceId: service.TipoId }, r => {
            if (r.success) setSpecialties(r.data || []);
            setLoading(false);
            nextStep();
        });
    };

    const handleSpecialtySelect = (specialty) => {
        setSelection({ ...selection, specialty, specialist: null, time: null });
        setLoading(true);
        request.Get(API_BASE + 'web/specialists', { specialtyId: specialty.EspecialidadId }, r => {
            if (r.success) setSpecialists(r.data || []);
            setLoading(false);
            nextStep();
        });
    };

    const handleSpecialistSelect = (specialist) => {
        setSelection({ ...selection, specialist, time: null });
        loadSlots(specialist.EspecialistaId, selection.date);
        nextStep();
    };

    const loadSlots = (specialistId, date) => {
        setLoading(true);
        request.Get(API_BASE + 'web/slots', { especialistaId: specialistId, fecha: date }, r => {
            if (r.success) setSlots(r.slots || []);
            setLoading(false);
        });
    };

    const handleDateChange = (date) => {
        setSelection({ ...selection, date, time: null });
        loadSlots(selection.specialist.EspecialistaId, date);
    };

    const handleFinish = () => {
        setLoading(true);
        const bookingData = {
            EspecialistaId: selection.specialist.EspecialistaId,
            EspecialidadId: selection.specialty.EspecialidadId,
            SucursalId: sucursalId,
            TurnoFch: selection.date,
            TurnoHra: selection.time,
            ClienteEmail: selection.client.email,
            ClienteNombre: selection.client.name,
            ClienteDni: selection.client.dni
        };

        request.Post(API_BASE + 'web/book', bookingData, r => {
            if (r.success) {
                setStep(6); // Success screen
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al agendar el turno' });
            }
            setLoading(false);
        });
    };

    // Components for each step
    const renderStep = () => {
        switch (step) {
            case 1: return (
                <div className="wizard-step">
                    <h3 className="wizard-title">Selecciona un Servicio</h3>
                    <p className="wizard-subtitle">¿Qué trámite deseas realizar hoy?</p>
                    <div className="wizard-grid">
                        {services.map(s => (
                            <motion.div 
                                whileHover={{ scale: 1.05 }} 
                                whileTap={{ scale: 0.95 }}
                                key={s.TipoId} 
                                className="wizard-card"
                                onClick={() => handleServiceSelect(s)}
                            >
                                <div className="card-icon"><FontAwesomeIcon icon={faTags} /></div>
                                <span className="card-label">{s.TipoNombre}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
            case 2: return (
                <div className="wizard-step">
                    <h3 className="wizard-title">Elige una Especialidad</h3>
                    <p className="wizard-subtitle">{selection.service?.TipoNombre}</p>
                    <div className="wizard-list">
                        {specialties.map(spec => (
                            <div key={spec.EspecialidadId} className="wizard-list-item" onClick={() => handleSpecialtySelect(spec)}>
                                <div>
                                    <div className="item-title">{spec.EspecialidadNombre}</div>
                                    <div className="item-desc">{spec.EspecialidadDescripcion}</div>
                                </div>
                                <FontAwesomeIcon icon={faChevronRight} />
                            </div>
                        ))}
                    </div>
                </div>
            );
            case 3: return (
                <div className="wizard-step">
                    <h3 className="wizard-title">Selecciona Profesional</h3>
                    <div className="wizard-grid">
                        {specialists.map(esp => (
                            <motion.div 
                                whileHover={{ scale: 1.05 }}
                                key={esp.EspecialistaId} 
                                className="wizard-card specialist-card"
                                onClick={() => handleSpecialistSelect(esp)}
                            >
                                <div className="card-avatar">
                                    <FontAwesomeIcon icon={faUserTie} />
                                </div>
                                <span className="card-label">{esp.EspecialistaNombre} {esp.EspecialistaApellido}</span>
                                <span className="card-tag">{esp.EspecialistaModalidad}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            );
            case 4: return (
                <div className="wizard-step">
                    <h3 className="wizard-title">Fecha y Hora</h3>
                    <div className="calendar-container">
                        <input 
                            type="date" 
                            className="wizard-date-picker mb-4" 
                            min={new Date().toISOString().split('T')[0]}
                            value={selection.date}
                            onChange={(e) => handleDateChange(e.target.value)}
                        />
                        
                        <div className="slots-grid">
                            {loading ? <Spinner animation="border" /> : slots.length === 0 ? (
                                <p className="text-muted">No hay horarios disponibles para este día.</p>
                            ) : (
                                slots.map(slot => (
                                    <button 
                                        key={slot} 
                                        className={`slot-btn ${selection.time === slot ? 'active' : ''}`}
                                        onClick={() => setSelection({ ...selection, time: slot })}
                                    >
                                        <FontAwesomeIcon icon={faClock} className="mr-2" /> {slot}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                    {selection.time && (
                        <motion.button 
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            className="auth-btn-primary mt-4" 
                            onClick={nextStep}
                        >
                            Continuar <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
                        </motion.button>
                    )}
                </div>
            );
            case 5: return (
                <div className="wizard-step">
                    <h3 className="wizard-title">Tus Datos</h3>
                    <p className="wizard-subtitle">Casi listo. Necesitamos tus datos para el recordatorio.</p>
                    <div className="wizard-form px-3">
                        <div className="mb-3">
                            <label className="small text-muted"><FontAwesomeIcon icon={faIdCard} className="mr-2" /> Nombre Completo</label>
                            <input 
                                type="text" className="turnero-input" placeholder="JUAN PEREZ"
                                value={selection.client.name} onChange={e => setSelection({...selection, client: {...selection.client, name: e.target.value.toUpperCase()}})} 
                            />
                        </div>
                        <div className="mb-3">
                            <label className="small text-muted"><FontAwesomeIcon icon={faEnvelope} className="mr-2" /> Correo Electrónico</label>
                            <input 
                                type="email" className="turnero-input" placeholder="tu@email.com"
                                value={selection.client.email} onChange={e => setSelection({...selection, client: {...selection.client, email: e.target.value}})} 
                            />
                        </div>
                        <div className="mb-4">
                            <label className="small text-muted">DNI / Identificación</label>
                            <input 
                                type="text" className="turnero-input" placeholder="Número de documento"
                                value={selection.client.dni} onChange={e => setSelection({...selection, client: {...selection.client, dni: e.target.value}})} 
                            />
                        </div>
                        
                        <div className="booking-summary mb-4">
                            <div className="small text-muted mb-2">Resumen de tu turno:</div>
                            <div className="summary-badge">
                                <FontAwesomeIcon icon={faCalendarCheck} className="mr-2" />
                                {selection.date} a las {selection.time} con {selection.specialist?.EspecialistaNombre}
                            </div>
                        </div>

                        <button 
                            className="auth-btn-primary" 
                            disabled={!selection.client.email || !selection.client.name || loading}
                            onClick={handleFinish}
                        >
                            {loading ? <Spinner size="sm" /> : 'Confirmar Reserva'}
                        </button>
                    </div>
                </div>
            );
            case 6: return (
                <div className="wizard-step text-center py-5">
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
                        <FontAwesomeIcon icon={faCheckCircle} size="5x" className="text-success mb-4" />
                    </motion.div>
                    <h2 className="font-weight-bold mb-3">¡Reserva Solicitada!</h2>
                    <p className="text-muted mb-4 px-4">
                        Hemos enviado un enlace de confirmación a <strong>{selection.client.email}</strong>.<br/> 
                        Por favor, verifica tu bandeja de entrada para validar tu turno.
                    </p>
                    <button className="turnero-btn-glass px-4" onClick={() => window.location.reload()}>
                        Volver al inicio
                    </button>
                </div>
            );
            default: return null;
        }
    };

    return (
        <div className="web-booking-container">
            <div className="booking-glass-panel">
                <div className="booking-header">
                    <div className="header-logo">TuFila</div>
                    {step > 1 && step < 6 && (
                        <button className="btn-back" onClick={prevStep}>
                            <FontAwesomeIcon icon={faChevronLeft} /> Volver
                        </button>
                    )}
                </div>

                <div className="booking-body">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            {renderStep()}
                        </motion.div>
                    </AnimatePresence>
                </div>
                
                {step < 6 && (
                    <div className="booking-footer">
                        <div className="step-indicator">
                            {[1, 2, 3, 4, 5].map(s => (
                                <div key={s} className={`dot ${step >= s ? 'active' : ''}`}></div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WebBookingWizard;
